import { ReactNode } from 'react';
import LinkIcon from '@mui/icons-material/Link';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import { Avatar, Button, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { css } from '@emotion/react';
import { colors } from '../../../theme/colors';
import { ISourcingProfileData } from '../data/sourcingProfileMockData';
import CrunchbaseMediaIcon from '../../../assets/icons/crunchbase-logo.svg?react';
import HarmonicMediaIcon from '../../../assets/icons/harmonic-logo.svg?react';
import { AddToListFromProfileAction } from './AddToListFromProfileAction';
import { RoundNameRenderer } from '../../../components/AgTable/cell-renderers/RoundRenderer';

const headerWrapperCss = css`
  background: #f9f9fe;
  border-bottom: 1px solid ${colors.neutral[10]};
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const topRowCss = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const companyInfoCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  min-height: 1px;
`;

const nameSectionCss = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  min-height: 1px;
  flex: 1;
`;

const nameRowCss = css`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const nameWithLinkCss = css`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const titleCss = css`
  font-size: 24px;
  font-weight: 400;
  line-height: 1.334;
  color: ${colors.text.main};
  white-space: nowrap;
`;

const websiteLinkCss = css`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.secondary};
  text-decoration: none;
  &:hover {
    color: ${colors.primary[60]};
  }
`;

const vendorStackCss = css`
  display: flex;
  align-items: center;
  padding-right: 6px;
  flex-shrink: 0;
`;

const vendorIconCss = css`
  width: 16px;
  height: 16px;
  border-radius: 100px;
  overflow: hidden;
  background: white;
  flex-shrink: 0;
`;

const actionsCss = css`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  justify-content: flex-end;
`;

const enrichedTextCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const actionsButtonRowCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const bottomSectionCss = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const descriptionCss = css`
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.17px;
  color: ${colors.text.main};
`;

const tagsRowCss = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const pillBaseCss = css`
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 12px;
  line-height: 1.66;
  white-space: nowrap;
  flex-shrink: 0;
`;

/** "Acquired (Apple)" — Figma blue/10 bg + blue/60 text. */
const acquiredPillCss = css`
  ${pillBaseCss}
  background: ${colors.primary[10]};
  color: ${colors.primary[60]};
`;

/** "Operating" — Figma cyan family; reuses tertiary tokens. */
const operatingPillCss = css`
  ${pillBaseCss}
  background: ${colors.tertiary[10]};
  color: ${colors.tertiary[70]};
`;

const modelChipCss = css`
  ${pillBaseCss}
  background: rgba(75, 82, 93, 0.08);
  color: ${colors.text.main};
`;

const sectorChipCss = css`
  display: inline-flex;
  align-items: center;
  background: ${colors.secondary[10]};
  color: ${colors.secondary[60]};
  border-radius: 100px;
  padding: 0 8px;
  font-size: 12px;
  line-height: 1.66;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
`;

const avatarSx = {
  width: 50,
  height: 50,
  bgcolor: colors.primary[10],
  color: colors.primary[60],
  fontSize: 20,
  fontWeight: 700,
  border: `1px solid ${colors.neutral[10]}`,
  borderRadius: '50%',
} as const;

/** Business model values to surface as grey chips, extracted from the tags array. */
const BUSINESS_MODEL_TAGS = new Set(['B2B', 'B2C', 'B2B/B2C']);

interface SourcingProfileHeaderProps {
  readonly data: ISourcingProfileData;
  /** Route segment the page was loaded with (`:companyId`). Forwarded to
   *  the membership-lookup hook so it shares the same cache key shape as
   *  `useSourcingProfile`. Falls back to `data.id` when not provided
   *  (legacy callers / tests). */
  readonly companyId?: string;
  /** Replaces the add-to-list action, for a sample profile that must not read the user's lists. */
  readonly addToListAction?: ReactNode;
}

export function SourcingProfileHeader({ data, companyId, addToListAction }: SourcingProfileHeaderProps) {
  const businessModel = data.tags.find((t) => BUSINESS_MODEL_TAGS.has(t)) ?? null;
  const latestFundingStage = data.fundingRounds[0]?.stage ?? null;

  const websiteUrl =
    data.website != null
      ? data.website.startsWith('http')
        ? data.website
        : `https://${data.website}`
      : null;

  const enrichedText = [
    `Enriched: ${data.enrichedDate}`,
    data.listsCount != null ? `Added to ${data.listsCount} list${data.listsCount === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join('. ');

  const operationalPill = buildOperationalPill(data);

  return (
    <div css={headerWrapperCss}>
      <div css={topRowCss}>
        <div css={companyInfoCss}>
          <Avatar src={data.logoUrl ?? undefined} alt={data.name} sx={avatarSx}>
            {data.name.charAt(0)}
          </Avatar>
          <div css={nameSectionCss}>
            <div css={nameRowCss}>
              <div css={nameWithLinkCss}>
                <Typography component='h1' css={titleCss}>
                  {data.name}
                </Typography>
                {websiteUrl != null && (
                  <Tooltip title={websiteUrl}>
                    <a
                      css={websiteLinkCss}
                      href={websiteUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      aria-label={`Visit ${data.name} website`}
                    >
                      <LinkIcon sx={{ fontSize: 14 }} />
                    </a>
                  </Tooltip>
                )}
              </div>

              <VendorSourceIcons data={data} />

              {latestFundingStage != null && latestFundingStage.toUpperCase() !== 'UNKNOWN' && (
                <RoundNameRenderer
                  name={latestFundingStage}
                  style={{
                    margin: 0,
                    width: 'auto',
                    height: 'auto',
                    minWidth: 'auto',
                    borderRadius: 20,
                    padding: '2px 10px',
                    fontSize: 12,
                    lineHeight: 1.66,
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div css={actionsCss}>
          <Typography css={enrichedTextCss}>{enrichedText}</Typography>
          <div css={actionsButtonRowCss}>
            <Button variant='outlined' color='secondary' startIcon={<ReadMoreIcon sx={{ fontSize: 16 }} />}>
              Enrich Now
            </Button>
            {addToListAction ?? (
              <AddToListFromProfileAction
                companyId={companyId ?? data.id}
                name={data.name}
                website={data.website}
                linkedinUrl={data.linkedinUrl}
                sector={data.sector}
                industry={data.industry}
              />
            )}
          </div>
        </div>
      </div>

      <div css={bottomSectionCss}>
        <Typography component='p' css={descriptionCss}>
          {data.description}
        </Typography>
        <div css={tagsRowCss}>
          {operationalPill}
          {businessModel != null && <span css={modelChipCss}>{businessModel}</span>}
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {data.sector.map((s) => (
              <span key={s} css={sectorChipCss}>
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Build the operational-status pill that lives in the meta row below the name.
 * Mirrors the Figma "Acquired (Apple)" / "Operating" patterns.
 */
function buildOperationalPill(data: ISourcingProfileData): React.ReactNode {
  const status = data.operationalStatus?.trim();
  if (!status) {
    return null;
  }

  const lower = status.toLowerCase();
  if (lower === 'acquired') {
    const label = data.acquiredBy ? `Acquired (${data.acquiredBy})` : 'Acquired';
    return <span css={acquiredPillCss}>{label}</span>;
  }
  if (lower === 'operating') {
    return <span css={operatingPillCss}>Operating</span>;
  }
  return <span css={modelChipCss}>{status}</span>;
}

interface VendorSourceIconsProps {
  readonly data: ISourcingProfileData;
}

interface VendorIconEntry {
  readonly id: string;
  readonly icon: React.ReactElement;
}

function VendorSourceIcons({ data }: VendorSourceIconsProps) {
  const sources: VendorIconEntry[] = (
    [
      data.crunchbaseUrl != null
        ? { id: 'crunchbase', icon: <CrunchbaseMediaIcon width={16} height={16} /> }
        : null,
      data.harmonicUrl != null
        ? { id: 'harmonic', icon: <HarmonicMediaIcon width={16} height={16} /> }
        : null,
    ] as const
  ).filter((entry): entry is VendorIconEntry => entry != null);

  if (sources.length === 0) {
    return null;
  }

  return (
    <div css={vendorStackCss}>
      {sources.map((entry, idx) => (
        <div
          key={entry.id}
          css={vendorIconCss}
          style={{
            marginRight: idx < sources.length - 1 ? -6 : 0,
            zIndex: sources.length - idx,
            position: 'relative',
          }}
        >
          {entry.icon}
        </div>
      ))}
    </div>
  );
}

export function SourcingProfileHeaderSkeleton() {
  return (
    <div css={headerWrapperCss}>
      <Stack direction='row' gap={1} alignItems='center'>
        <Skeleton variant='circular' width={50} height={50} sx={{ flexShrink: 0 }} />
        <Stack gap={0.5} flex={1} alignItems='flex-start'>
          <Stack direction='row' gap={1.5} alignItems='center'>
            <Skeleton variant='text' width={180} height={32} />
            <Skeleton variant='rounded' width={70} height={22} sx={{ borderRadius: '20px' }} />
          </Stack>
        </Stack>
        <Stack direction='row' gap={1} alignItems='center' sx={{ flexShrink: 0 }}>
          <Skeleton variant='text' width={160} height={16} />
          <Skeleton variant='rounded' width={100} height={30} sx={{ borderRadius: '4px' }} />
          <Skeleton variant='rounded' width={96} height={30} sx={{ borderRadius: '4px' }} />
        </Stack>
      </Stack>
      <Stack gap={0.75} mt={0.25}>
        <Skeleton variant='text' width='100%' height={20} />
        <Stack direction='row' gap={1.5} flexWrap='wrap'>
          <Skeleton variant='rounded' width={100} height={20} sx={{ borderRadius: '4px' }} />
          <Skeleton variant='rounded' width={40} height={20} sx={{ borderRadius: '4px' }} />
          <Skeleton variant='rounded' width={160} height={20} sx={{ borderRadius: '100px' }} />
          <Skeleton variant='rounded' width={80} height={20} sx={{ borderRadius: '100px' }} />
        </Stack>
      </Stack>
    </div>
  );
}
